import { Activity, BarChart3, LayoutGrid, Lightbulb, MapPin, Users2 } from 'lucide-react';

import { greyP5 } from '../../../theme';
import { AnalyticsSubTab } from '../../../types/analytics';

interface AnalyticsSidebarProps {
  activeTab: AnalyticsSubTab;
  onTabChange: (tab: AnalyticsSubTab) => void;
}

interface Category {
  name: AnalyticsSubTab;
  label: string;
  icon: typeof Activity;
  enabled: boolean;
  blurb: string;
}

const CATEGORIES: Category[] = [
  {
    name: 'my-luminaires',
    label: 'My Luminaires',
    icon: Lightbulb,
    enabled: true,
    blurb: 'Full catalog list with usage metrics',
  },
  {
    name: 'luminaire-performance',
    label: 'Luminaire Performance',
    icon: Activity,
    enabled: true,
    blurb: 'Compare any two luminaires across 8 axes',
  },
  {
    name: 'overview',
    label: 'Portfolio Overview',
    icon: BarChart3,
    enabled: false,
    blurb: 'Catalog-wide KPIs',
  },
  {
    name: 'portfolio-gap',
    label: 'Portfolio Gap',
    icon: LayoutGrid,
    enabled: false,
    blurb: 'Find under-served project types',
  },
  {
    name: 'regional',
    label: 'Regional Insights',
    icon: MapPin,
    enabled: false,
    blurb: 'Country & climate-zone heatmap',
  },
  {
    name: 'lead-activity',
    label: 'Lead Activity',
    icon: Users2,
    enabled: false,
    blurb: 'Inquiry funnel & lead breakdown',
  },
];

export const AnalyticsSidebar = ({ activeTab, onTabChange }: AnalyticsSidebarProps) => {
  return (
    <nav
      className="flex-shrink-0 hidden md:inline-flex flex-col border-r"
      style={{
        width: '240px',
        height: 'calc(100vh - 224px)',
        borderColor: greyP5,
      }}
      data-testid="analytics-sidebar-nav"
    >
      <div className="bg-black overflow-y-auto h-full">
        <div className="flex flex-col">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const active = activeTab === cat.name;
            return (
              <button
                key={cat.name}
                onClick={() => cat.enabled && onTabChange(cat.name)}
                disabled={!cat.enabled}
                className={`flex flex-row items-center justify-between w-full min-h-[44px] gap-2 text-left px-6 text-white font-medium
                  disabled:cursor-not-allowed
                  border-b border-greyP5 transition-colors duration-200 text-xs ${
                    active ? 'bg-greyP5' : 'hover:bg-greyP5'
                  }`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Icon size={14} className={active ? 'text-green01' : 'text-greyP2'} />
                  <span className={active ? 'text-green01' : ''}>{cat.label}</span>
                </div>
                {!cat.enabled && (
                  <span className="bg-greyP5 text-black text-[10px] px-2 font-extrabold p-1 rounded-full">Soon</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="p-4 mt-2">
          <div className="rounded-lg border border-green04/30 bg-green04/5 p-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-green01 mb-1">Admin only</div>
            <p className="text-[11px] text-greyP2 leading-relaxed">
              This view is hidden from lighting designers. Data is mocked for the demo — backend integration is pending.
            </p>
          </div>
        </div>
      </div>
    </nav>
  );
};
